// nuolino-admin/src/components/layout/MobileDrawer.tsx
// Drawer para < md. Backdrop + trap de foco + ESC + close automático.

import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui';

export interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  className?: string;
}

export function MobileDrawer({ open, onClose, children, title, className }: MobileDrawerProps) {
  const { t } = useTranslation();
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const ariaLabel = title ?? t('common.menu');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    drawerRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-modal md:hidden" role="dialog" aria-modal="true" aria-label={ariaLabel}>
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onClose}
        className="absolute inset-0 bg-neutral-900/50"
        tabIndex={-1}
      />
      <div
        ref={drawerRef}
        tabIndex={-1}
        className={cn('relative flex h-full w-72 max-w-[85vw] flex-col bg-surface shadow-level3 outline-none', className)}
      >
        <div className="flex h-16 items-center justify-between border-b border-border-subtle px-4">
          <span className="text-lg font-semibold text-text">{title ?? t('common.menu')}</span>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('common.close')} leftIcon={<X className="h-5 w-5" />}>
            <span className="sr-only">{t('common.close')}</span>
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}
